import type { Invoice, InvoiceTransitionAction } from '@/types';

/**
 * Zellen-Editierbarkeit im Grid — Spiegel der EDITABLE-Whitelist im
 * InvoiceController. Bauleiter/Vertrieb sehen die Tabelle rein lesend;
 * die Review-Aktionen (freigeben/Korrektur/ablehnen) laufen im
 * Detail-Drawer.
 */
export type GridField =
  | 'invoice_number'
  | 'invoice_date'
  | 'due_date'
  | 'description'
  | 'notes'
  | 'contact_id'
  | 'bauvorhaben_id'
  | 'gewerk_id'
  | 'amount_net'
  | 'amount_tax'
  | 'amount_gross'
  | 'tax_rate_percent'
  | 'file'
  | 'payment';

// Felder, die auch nach der formalen Prüfung noch anpassbar sind
// (Zuordnungen + Betreff + Doku/Notizen; Spiegel der EDITABLE-Whitelist
// im Controller).
const ASSIGNMENT_FIELDS: GridField[] = ['contact_id', 'bauvorhaben_id', 'gewerk_id', 'description', 'notes'];

export function canEditCell(
  invoice: Pick<Invoice, 'status'>,
  field: GridField,
  isOffice: boolean
): boolean {
  if (!isOffice) return false;
  // Der MwSt-Betrag ist in keinem Status manuell editierbar (Produktregel:
  // berechnet aus Netto/Brutto und Satz — Tooltip erklärt das).
  if (field === 'amount_tax') return false;
  switch (invoice.status) {
    case 'neu_eingegangen':
    case 'erfasst':
      return true; // alle Felder inkl. Bezahlt
    case 'formal_geprueft':
      return ASSIGNMENT_FIELDS.includes(field);
    case 'korrektur_erforderlich':
      return ASSIGNMENT_FIELDS.includes(field) || field === 'file';
    case 'freigegeben':
    case 'an_lexware_uebergeben':
    case 'abgelehnt':
      return false;
    default:
      return false;
  }
}

/** Bezahlt-Toggle ist Zahlungs-Recht — nur Finanzen/Admin (Transition-Mark). */
export function canTogglePaid(isFinanzen: boolean): boolean {
  return isFinanzen;
}

/** Löschbarkeit = Whitelist im destroy() (neu_eingegangen/erfasst/abgelehnt). */
export function canDelete(invoice: Pick<Invoice, 'status'>, isOffice: boolean): boolean {
  return isOffice && (invoice.status === 'neu_eingegangen' || invoice.status === 'erfasst' || invoice.status === 'abgelehnt');
}

/**
 * MwSt-Autoableitung (Phase 3, bindende Produktregeln):
 *  - Der MwSt-Betrag ist NIE manuell editierbar — der Client leitet ihn
 *    ab und schickt alle betroffenen Felder in EINEM PUT (der Server
 *    leitet nichts ab, er prüft nur net + tax ≈ gross, ε 0.02).
 *  - Ein fehlender MwSt-Satz gilt als 0 % (Satz leer → MwSt 0,
 *    Brutto = Netto); setzt man später einen Satz, rechnet das Brutto neu.
 * Runden auf Cent.
 */
const r2 = (x: number): number => Math.round(x * 100) / 100;

/** Wirksamer Satz: NULL ≡ 0 % (Produktregel). */
export const effectiveRate = (ratePercent: number | null): number =>
  Number.isFinite(ratePercent as number) ? (ratePercent as number) : 0;

export function deriveNetTax(gross: number, ratePercent: number): { net: number; tax: number } | null {
  if (!Number.isFinite(gross) || !Number.isFinite(ratePercent) || gross <= 0) return null;
  const net = r2(gross / (1 + ratePercent / 100));
  const tax = r2(gross - net);
  return { net, tax };
}

/** Betrags-/Satz-Stand einer Rechnungszeile (Drafts reichen Strings nach). */
export interface AmountRowState {
  amount_net: number | null;
  amount_tax: number | null;
  amount_gross: number | null;
  tax_rate_percent: number | null;
}

export type AmountChangedField = 'amount_net' | 'amount_gross' | 'tax_rate_percent';

/**
 * Ein Betrags-/Satz-Feld hat sich geändert → Payload mit allen
 * betroffenen Feldern (ein PUT) oder null (nichts abzuleiten — der
 * Aufrufer speichert dann nur das geänderte Feld).
 *
 *   Netto   geändert → MwSt = net·r, Brutto = net + MwSt (Netto führt).
 *   Brutto  geändert → Netto = gross / (1 + r), MwSt = Brutto − Netto.
 *   Satz    geändert → Netto bleibt fix (MwSt + Brutto neu); ohne Netto,
 *                      aber mit Brutto: Netto/MwSt aus dem Brutto.
 */
export function deriveAmounts(
  changed: AmountChangedField,
  rawValue: number | null,
  row: AmountRowState
): { payload: Partial<Record<string, number | null>>; fields: GridField[] } | null {
  if (changed === 'amount_net') {
    const net = rawValue;
    if (net == null || !Number.isFinite(net)) return null;
    const rate = effectiveRate(row.tax_rate_percent);
    const tax = r2((net * rate) / 100);
    const gross = r2(net + tax);
    return {
      payload: { amount_net: net, amount_tax: tax, amount_gross: gross },
      fields: ['amount_net', 'amount_tax', 'amount_gross'],
    };
  }

  if (changed === 'amount_gross') {
    const gross = rawValue;
    const rate = effectiveRate(row.tax_rate_percent);
    const derived = gross != null && Number.isFinite(gross) ? deriveNetTax(gross, rate) : null;
    if (!derived) return null;
    return {
      payload: { amount_gross: gross, amount_net: derived.net, amount_tax: derived.tax },
      fields: ['amount_gross', 'amount_net', 'amount_tax'],
    };
  }

  // tax_rate_percent
  const rate = rawValue;
  if (rate == null || !Number.isFinite(rate) || rate < 0) return null;
  if (row.amount_net != null) {
    // Netto bleibt fix — Satz-Änderung rechnet MwSt + Brutto neu.
    const net = Number(row.amount_net);
    const tax = r2((net * rate) / 100);
    const gross = r2(net + tax);
    return {
      payload: { tax_rate_percent: rate, amount_tax: tax, amount_gross: gross },
      fields: ['tax_rate_percent', 'amount_tax', 'amount_gross'],
    };
  }
  if (row.amount_gross != null) {
    const derived = deriveNetTax(Number(row.amount_gross), rate);
    if (!derived) return null;
    return {
      payload: { tax_rate_percent: rate, amount_net: derived.net, amount_tax: derived.tax },
      fields: ['tax_rate_percent', 'amount_net', 'amount_tax'],
    };
  }
  return null; // weder Netto noch Brutto gesetzt → nur der Satz wird gespeichert
}

/** Statuswechsel, die direkt aus dem Grid möglich sind (Bezahlt-Achse). */
export function paidToggleAction(paid: boolean): InvoiceTransitionAction {
  return paid ? 'mark_paid' : 'mark_unpaid';
}